<script setup lang="ts">
import { useRoute } from 'vue-router/composables'
import ArrowLeft from '@/assets/icons/ArrowLeft.vue'
import BKInput from '@/components/AppShared/BKInput.vue'
import BKButton from '@/components/AppShared/BKButton.vue'
import { ref, computed } from 'vue'
import { mdiCheck, mdiChevronDown, mdiChevronUp, mdiMagnify } from '@mdi/js'
import QRCodeScanner from '@/features/tickets/ui/front-desk/QRCodeScanner.vue'
import { useFrontdeskQuery } from '@/features/tickets/api/queries'
import { centsToDollars } from '@/services/currency'
import CreditCard from '@/assets/icons/CreditCard.vue'
import CurrencyDollar from '@/assets/icons/CurrencyDollar.vue'
import CreditCardFilled from '@/assets/icons/CreditCardFilled.vue'
import CashPayment from '@/assets/icons/CashPayment.vue'
import AvatarPlaceholder from '@/features/tickets/assets/AvatarPlaceholder.vue'
import { useImage } from '@/shared/composables/image'
import AdminTicketModal from '@/features/tickets/ui/AdminTicketModal.vue'
import AddAttendeeModal from '@/features/tickets/ui/AddAttendeeModal.vue'
import { IFrontdeskTicket, IFrontdeskRegistrant } from '@/features/tickets/types'
import { ModalWrapper } from '@/shared/ui/modals'
import AdminUserTicketsInterface from '@/features/tickets/ui/AdminUserTicketsInterface.vue'
import { useEventQuery } from '@/enitites/event/api/queries'

const route = useRoute()
const { getImageById } = useImage()
const {
  data: frontdesk,
  isLoading: isFrontdeskLoading,
  error: frontdeskError,
} = useFrontdeskQuery(Number(route.params.eventId))
const { data: event } = useEventQuery(Number(route.params.eventId))

const searchQuery = ref('')
const showBreakdown = ref(false)

const isModalOpen = ref(false)
const selectedTicket = ref<IFrontdeskTicket | undefined>(undefined)
const selectedQRCode = ref<string | undefined>(undefined)

const isAddAttendeeModalOpen = ref(false)
const prefilledUser = ref<IFrontdeskRegistrant | undefined>(undefined)

const handleQRCodeDecode = (decodedString: string) => {
  selectedQRCode.value = decodedString
  selectedTicket.value = undefined
  isModalOpen.value = true
}

const openModalWithTicket = (ticket: IFrontdeskTicket) => {
  selectedTicket.value = ticket
  selectedQRCode.value = undefined
  isModalOpen.value = true
}

const closeModal = () => {
  isModalOpen.value = false
  selectedTicket.value = undefined
  selectedQRCode.value = undefined
}

const openAddAttendeeModal = (user?: IFrontdeskRegistrant) => {
  prefilledUser.value = user
  isAddAttendeeModalOpen.value = true
}

const closeAddAttendeeModal = () => {
  isAddAttendeeModalOpen.value = false
  prefilledUser.value = undefined
}

const handleAttendeeAdded = () => {
  closeAddAttendeeModal()
}

const handleManageTicket = (ticketId: number) => {
  const ticket = frontdesk.value?.tickets?.find((t) => t.id === ticketId)
  if (!ticket) return
  closeAddAttendeeModal()
  openModalWithTicket(ticket)
}

const filteredTickets = computed(() => {
  if (!frontdesk.value) return []
  const allRecords = [...(frontdesk.value.tickets || []), ...(frontdesk.value.battleRegistrantsNoTicket || [])]
  if (!searchQuery.value.trim()) return allRecords

  const query = searchQuery.value.toLowerCase().trim()
  return allRecords.filter((ticket) => {
    const dancerName =
      'assignedTo' in ticket
        ? ticket.assignedTo?.dancerName?.toLowerCase() || ''
        : ticket.dancerName?.toLowerCase() || ''
    const email =
      'assignedTo' in ticket ? ticket.assignedTo?.email?.toLowerCase() || '' : ticket.email?.toLowerCase() || ''
    return dancerName.includes(query) || email.includes(query)
  })
})
</script>

<template>
  <div class="frontdesk-page">
    <div class="content">
      <div class="page-header">
        <div class="page-header-top">
          <div class="page-header-left">
            <v-btn
              class="back-button"
              height="36"
              text
              :to="{ name: 'Event', params: { id: route.params.eventId } }"
            >
              <ArrowLeft />
              Back
            </v-btn>
            <h1 class="page-title">Front desk</h1>
          </div>
          <BKButton
            white-text
            width="auto"
            @click="() => openAddAttendeeModal()"
          >
            Add attendee
          </BKButton>
        </div>
        <div class="page-header-search">
          <BKInput
            v-model="searchQuery"
            placeholder="Search people"
            :append-icon="mdiMagnify"
          />
        </div>
        <div class="page-header-bottom">
          <div class="tickets-break-down">
            <button
              class="breakdown-title"
              @click="showBreakdown = !showBreakdown"
            >
              Tickets break down
              <v-icon class="breakdown-button-icon">
                {{ showBreakdown ? mdiChevronUp : mdiChevronDown }}
              </v-icon>
            </button>
            <div
              class="breakdown-items"
              :class="{ shown: showBreakdown }"
            >
              <div class="breakdown-item">
                <CreditCard />
                <v-skeleton-loader
                  v-if="isFrontdeskLoading"
                  type="image"
                  width="74"
                  height="24"
                />
                <template v-else-if="frontdesk">
                  {{ frontdesk.breakdownByPaymentType.creditCard.count }} =
                  {{ centsToDollars(frontdesk.breakdownByPaymentType.creditCard.amount) }}
                </template>
              </div>
              <div class="breakdown-item">
                <CurrencyDollar />
                <v-skeleton-loader
                  v-if="isFrontdeskLoading"
                  type="image"
                  width="74"
                  height="24"
                />
                <template v-else-if="frontdesk">
                  {{ frontdesk.breakdownByPaymentType.cash.count }} =
                  {{ centsToDollars(frontdesk.breakdownByPaymentType.cash.amount) }}
                </template>
              </div>
            </div>
          </div>
          <QRCodeScanner @decode="handleQRCodeDecode" />
        </div>
      </div>

      <div
        v-if="!frontdeskError"
        class="frontdesk-table-wrapper"
      >
        <table class="frontdesk-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Ticket</th>
              <th>Battles</th>
              <th>
                Checked in
                <v-skeleton-loader
                  v-if="isFrontdeskLoading"
                  type="image"
                  width="46"
                  height="24"
                  style="display: inline-block"
                />
                <template v-else-if="frontdesk">
                  {{
                    ` ${frontdesk.countCheckedIn}/${frontdesk.countTickets + frontdesk.countBattleRegistrantsNoTicket}`
                  }}
                </template>
              </th>
            </tr>
          </thead>
          <tbody v-if="frontdesk">
            <template v-for="ticket in filteredTickets">
              <tr
                v-if="'id' in ticket"
                :key="ticket.id"
                class="clickable-row"
                @click="openModalWithTicket(ticket)"
              >
                <td>
                  <div class="user-info">
                    <AvatarPlaceholder
                      v-if="!ticket.assignedTo?.avatar"
                      background-color="#DDDDDD"
                      color="#333333"
                      size="32"
                    />
                    <v-avatar
                      v-else
                      size="32"
                    >
                      <v-img :src="getImageById(ticket.assignedTo.avatar, 64, 64, 'avatar')" />
                    </v-avatar>
                    <span class="user-name">
                      {{ ticket.assignedTo?.dancerName || ticket.assignedTo?.email || 'Unassigned' }}
                    </span>
                  </div>
                </td>
                <td>
                  <div
                    v-if="ticket.title"
                    class="ticket-info"
                  >
                    <span class="ticket-title">
                      {{ ticket.title }}
                    </span>
                    <span class="divider" />
                    <span class="ticket-price">
                      {{ centsToDollars(ticket.price) }}
                    </span>
                    <span class="divider" />
                    <CreditCardFilled v-if="ticket.paymentType === 'creditCard'" />
                    <CashPayment v-if="ticket.paymentType === 'cash'" />
                    <span
                      v-if="ticket.paymentType === 'guest'"
                      class="payment-type-guest"
                      >Guest</span
                    >
                  </div>
                  <span v-else> Purchase tickets </span>
                </td>
                <td>{{ ticket.battles.countRegistered }}</td>
                <td>
                  <span
                    v-if="ticket.isRefunded"
                    class="checkin refunded"
                  >
                    Refunded
                  </span>
                  <span
                    v-else-if="ticket.isCheckedIn"
                    class="checkin checked"
                  >
                    <v-icon color="var(--color-green)">{{ mdiCheck }}</v-icon>
                    Checked in
                  </span>
                  <span
                    v-else
                    class="checkin not-checked"
                  >
                    Not checked in
                  </span>
                </td>
              </tr>
              <tr
                v-else-if="'userId' in ticket"
                :key="ticket.userId"
                class="clickable-row"
                @click="openAddAttendeeModal(ticket)"
              >
                <td>
                  <div class="user-info">
                    <AvatarPlaceholder
                      v-if="!ticket.avatar"
                      background-color="#DDDDDD"
                      color="#333333"
                      size="32"
                    />
                    <v-avatar
                      v-else
                      size="32"
                    >
                      <v-img :src="getImageById(ticket.avatar, 64, 64, 'avatar')" />
                    </v-avatar>
                    <span class="user-name">
                      {{ ticket.dancerName || ticket.email || 'Unassigned' }}
                    </span>
                  </div>
                </td>
                <td>Purchase tickets</td>
                <td>
                  <span
                    v-if="event?.paymentSystem?.isEnabled"
                    class="upgrade-required"
                  >
                    ({{ ticket.battles.countRegistered }}): Ticket needed
                  </span>
                  <span v-else> {{ ticket.battles.countRegistered }} </span>
                </td>
                <td>
                  <span class="checkin not-checked"> Not checked in </span>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div
        v-if="frontdeskError"
        class="content-error"
      >
        <p>Error loading front desk data: {{ frontdeskError.message }}</p>
      </div>
    </div>

    <AdminTicketModal
      v-if="isModalOpen && !selectedTicket?.assignedTo?.userId"
      v-model="isModalOpen"
      :ticket-id="selectedTicket?.id"
      :qr-code="selectedQRCode"
      :event-id="Number(route.params.eventId)"
      @close-modal="closeModal"
    />

    <ModalWrapper
      v-else-if="isModalOpen && selectedTicket?.assignedTo?.userId"
      :is-show.sync="isModalOpen"
      modal-width="904px"
    >
      <AdminUserTicketsInterface
        :event-id="Number(route.params.eventId)"
        :initial-ticket="selectedTicket"
        @close="closeModal"
      />
    </ModalWrapper>

    <AddAttendeeModal
      v-if="isAddAttendeeModalOpen"
      v-model="isAddAttendeeModalOpen"
      :event-id="Number(route.params.eventId)"
      :prefilled-user="prefilledUser"
      @close-modal="closeAddAttendeeModal"
      @attendee-added="handleAttendeeAdded"
      @manage-ticket="handleManageTicket"
    />
  </div>
</template>

<style scoped lang="scss">
@import '@/assets/style/mixins';

.frontdesk-page {
  padding: 80px 0 0;
  background: var(--color-grey-100);

  p {
    margin: 0;
  }

  .content {
    max-width: 1190px;
    margin: 0 auto;
    padding: 40px;

    @media (max-width: 600px) {
      padding: 20px;
    }
  }

  .content-error {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 400px;
    font-size: 18px;
    color: var(--error-red);
  }
}

.page-header {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;

  .page-header-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 20px;
    margin-bottom: 12px;

    .page-header-left {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;

      .back-button {
        text-transform: none;
        background-color: transparent;
        padding: 0 8px 0 0;
        min-width: 0;
        font-weight: 600;
        font-size: 20px;
        line-height: 36px;

        &::before {
          opacity: 0;
        }

        &:hover::before,
        &:focus-visible::before {
          opacity: 0.18;
        }
      }

      .page-title {
        @include font-h2;

        text-transform: uppercase;

        @media (max-width: 600px) {
          @include font-h3;
        }
      }
    }
  }

  .page-header-search {
    display: flex;
    gap: 12px;
    align-items: center;
    margin-bottom: 24px;
  }

  .page-header-bottom {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 20px;
  }

  .tickets-break-down {
    display: flex;
    flex-direction: column;
    gap: 18px;

    .breakdown-title {
      display: flex;
      align-items: center;
      gap: 8px;
      width: max-content;
      text-align: left;
      font: inherit;
      font-size: 16px;
      line-height: 24px;
      color: var(--color-dark-800);
      cursor: default;
      pointer-events: none;

      @media (max-width: 600px) {
        cursor: pointer;
        pointer-events: auto;
      }

      .breakdown-button-icon {
        display: none;

        @media (max-width: 600px) {
          display: inline-block;
        }
      }
    }

    .breakdown-items {
      display: flex;
      gap: 12px 46px;

      @media (max-width: 600px) {
        flex-wrap: wrap;
        height: 0;
        overflow: hidden;
        transition: height 0.3s ease;

        &.shown {
          height: calc-size(auto, size);
        }
      }

      .breakdown-item {
        display: flex;
        align-items: center;
        gap: 6px;
        font-weight: 700;
        font-size: 16px;
        line-height: 24px;
        color: var(--color-dark-800);
      }
    }
  }
}

.frontdesk-table-wrapper {
  overflow-x: auto;
  scroll-timeline: --table-scroll-timeline inline;
}

.frontdesk-table {
  width: 100%;
  border-collapse: collapse;

  thead {
    th {
      text-align: left;
      padding: 20px 0 40px;
      font-weight: 500;
      font-size: 20px;
      line-height: 32px;
      color: var(--color-dark-800);
      background-color: var(--color-grey-100);
      white-space: nowrap;

      &:first-child {
        position: sticky;
        left: 0;

        @media (max-width: 600px) {
          animation: frozen-column-width;
          animation-timeline: --table-scroll-timeline;

          &::after {
            content: '';
            position: absolute;
            top: -1px;
            right: -4px;
            bottom: 0;
            width: 4px;
            height: calc(100% + 1px);
            background-image: linear-gradient(to right, rgb(0 0 0 / 20%), transparent);
            animation: frozen-column-shadow;
            animation-timeline: --table-scroll-timeline;
          }
        }
      }

      &:not(:last-child) {
        padding-right: 20px;
      }

      &:last-child {
        text-align: right;
      }
    }
  }

  tbody {
    tr {
      border-top: 1px solid var(--color-grey-600);

      &:first-child {
        border-top: none;
      }

      &.clickable-row {
        cursor: pointer;

        &:hover {
          background-color: var(--color-grey-150);
        }
      }

      td {
        padding: 20px 0;
        font-size: 16px;
        line-height: 24px;
        color: var(--color-dark-800);
        background-color: var(--color-grey-100);

        span {
          min-width: max-content;
          white-space: nowrap;
        }

        &:first-child {
          position: sticky;
          left: 0;

          @media (max-width: 600px) {
            animation: frozen-column-width;
            animation-timeline: --table-scroll-timeline;

            &::after {
              content: '';
              position: absolute;
              top: -1px;
              right: -4px;
              bottom: 0;
              width: 4px;
              height: calc(100% + 1px);
              background-image: linear-gradient(to right, rgb(0 0 0 / 20%), transparent);
              animation: frozen-column-shadow;
              animation-timeline: --table-scroll-timeline;
            }
          }
        }

        &:not(:last-child) {
          padding-right: 20px;
        }

        &:last-child {
          text-align: right;
        }
      }
    }
  }
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 54px;
  width: 100%;
  overflow: hidden;

  > * {
    flex-shrink: 0;
  }

  .user-name {
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.ticket-info {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 54px;

  > * {
    flex-shrink: 0;
  }

  .ticket-price {
    font-weight: 500;
  }

  .divider {
    width: 1px;
    height: 24px;
    background-color: #d9d9d9;
  }
}

.payment-type-guest {
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  padding: 2px 8px;
  border-radius: 4px;
  background-color: var(--color-grey-600);
  color: var(--color-dark-800);
}

.upgrade-required {
  color: var(--error-red);
}

.checkin {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  font-size: 16px;
  line-height: 24px;

  > * {
    flex-shrink: 0;
  }

  &.checked {
    color: var(--color-green);
  }

  &.not-checked {
    color: var(--color-dark-800);
  }

  &.refunded {
    color: var(--error-red);
  }
}

@keyframes frozen-column-shadow {
  0% {
    opacity: 0;
  }

  10% {
    opacity: 1;
  }
}

@keyframes frozen-column-width {
  0% {
    max-width: 100vw;
  }

  10% {
    max-width: 35vw;
  }

  100% {
    max-width: 35vw;
  }
}
</style>
